import { useCallback, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';

import { isSuccessful } from '../services/api-requests';
import { notificationService } from '../services/notification-service';
import {
  createSetupIntent,
  setDefaultPaymentMethod,
} from '../services/payment-method-service';
import { PaymentMethodSummary } from '../models/payment-method-model';

export interface UseDefaultPaymentSetupResult {
  isPresenting: boolean;
  /**
   * On web the inline Elements form lives inside the `PaymentMethods` page,
   * so this hook only exposes the persistence step. After Elements confirms
   * the SetupIntent, the page hands the resulting `pm_...` id here so we can
   * PUT it as the user's default payment method.
   */
  presentSetupSheet: () => Promise<PaymentMethodSummary | null>;
  persistDefaultPaymentMethod: (
    paymentMethodId: string,
  ) => Promise<PaymentMethodSummary | null>;
  beginSetup: () => Promise<{ clientSecret: string } | null>;
  stripePromise: ReturnType<typeof loadStripe>;
}

const stripeKey = process.env.STRIPE_PUBLISHABLE_KEY ?? '';
const stripePromise = stripeKey ? loadStripe(stripeKey) : Promise.resolve(null);

export function useDefaultPaymentSetup(): UseDefaultPaymentSetupResult {
  const [isPresenting, setIsPresenting] = useState(false);

  const beginSetup = useCallback(async () => {
    setIsPresenting(true);
    try {
      const resp = await createSetupIntent();
      if (!isSuccessful(resp.status) || !resp.payload?.client_secret) {
        notificationService.sendNotification(
          'error',
          'Unable to start card setup. Please try again.',
        );
        return null;
      }
      return { clientSecret: resp.payload.client_secret };
    } catch (e: any) {
      console.error('[useDefaultPaymentSetup.web] beginSetup error:', e);
      notificationService.sendNotification(
        'error',
        e?.message ?? 'Unable to start card setup.',
      );
      return null;
    } finally {
      setIsPresenting(false);
    }
  }, []);

  const persistDefaultPaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<PaymentMethodSummary | null> => {
      try {
        const resp = await setDefaultPaymentMethod(paymentMethodId);
        if (!isSuccessful(resp.status) || !resp.payload) {
          notificationService.sendNotification(
            'error',
            'Card was saved but could not be set as default. Please retry.',
          );
          return null;
        }
        notificationService.sendNotification(
          'success',
          'Default payment method saved.',
        );
        return resp.payload.default_payment_method ?? null;
      } catch (e: any) {
        console.error('[useDefaultPaymentSetup.web] persist error:', e);
        notificationService.sendNotification(
          'error',
          e?.message ?? 'Could not save default payment method.',
        );
        return null;
      }
    },
    [],
  );

  // Kept for API parity with the native hook. The web implementation drives
  // Elements inline rather than presenting a modal sheet, so this is a no-op
  // surfacing instructions instead.
  const presentSetupSheet = useCallback(async () => {
    notificationService.sendNotification(
      'info',
      'Card setup on web is performed in-page.',
    );
    return null;
  }, []);

  return {
    isPresenting,
    presentSetupSheet,
    persistDefaultPaymentMethod,
    beginSetup,
    stripePromise,
  };
}
